import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormApiService } from '@app/core/services/form-api.service';
import { ChargeInformationDto, ChargeInformationService, ServiceRequestService, UserResponse } from '@app/api/dmc';
import { AlertService } from '@app/core/services/alert.service';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { TranslatePipe } from '@ngx-translate/core';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { Validator } from '@shared/validators/validator.wrapper';
import { catchError, forkJoin, Observable, of, Subject } from 'rxjs';
import { AppConfigService } from '@app/core/config/app-config.service';
import { ServiceRequestCode } from '@app/api/dmc/model/serviceRequestCode';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-dashboard-query-response',
  imports: [
    LabelDirective,
    InputGroupComponent,
    ReactiveFormsModule,
    TranslatePipe,
    InputDirective
  ],
  templateUrl: './dashboard-query-response.component.html',
  styleUrl: './dashboard-query-response.component.css'
})
export class DashboardQueryResponseComponent implements OnInit, HasUnsavedChanges {
  protected readonly UserResponse = UserResponse;
  private readonly destroy$ = new Subject<void>();

  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public chargeInformations: ChargeInformationDto[] = [];

  public form: FormGroup = new FormGroup({
    address: new FormControl(null),
    serviceRequestId: new FormControl(null, Validators.required),
    meteringPointIdentification: new FormControl(null, Validators.required),
    date: new FormControl(new Date().toISOString().substring(0, 10), [Validators.required, Validator.dateGreaterThanOrEqual(undefined, -7)]),
    response: new FormControl(null, Validators.required),
    reasonText: new FormControl(null),
    chargeInformationId: new FormControl(null)
  });

  public element?: any;
  public actionType?: 'disconnect' | 'disconnectDueToEndOfSupply' | 'reconnect' | 'closeDown' | 'metercontrol' |'serviceRequestAction' ;

  protected readonly environment = inject(AppConfigService).settings;

  constructor(
    private readonly formApiService: FormApiService,
    private readonly serviceRequestService: ServiceRequestService,
    private readonly chargeInformationService: ChargeInformationService,
    private readonly alertService: AlertService,
  ) {
  }

  ngOnInit(): void {
    if (this.element) {
      this.initializeAlerts();
      this.populateServiceRequestId();
      this.form.get('meteringPointIdentification')?.setValue(this.element.meteringPointIdentification);
    }

    this.loadChargeInformations();

    if (this.element?.address) {
      this.form.get('address')?.setValue(this.element.address);
    }
  }

  private initializeAlerts(): void {
    if (this.element.isCancelledBySupplier) {
      this.alertService.open('modal', 'info', ['dashboard.servicerequest.cancelledbysupplierwarning']);
    } else {
      const initMsg = this.getActionMessage();
      if (initMsg) {
        this.alertService.open('modal', 'info', initMsg);
      }
    }
  }

  private getActionMessage(): string {
    const messages: Record<string, string> = {
      disconnect: 'dashboard.servicerequest.disconnect.reply',
      disconnectDueToEndOfSupply: 'dashboard.servicerequest.disconnectDueToEndOfSupply.reply',
      reconnect: 'dashboard.servicerequest.reconnect.reply',
      closeDown: 'dashboard.servicerequest.closeDown.reply'
    };
    return messages[this.actionType || ''] || '';
  }

  private populateServiceRequestId(): void {
    const serviceRequestId = this.element.serviceRequestId ?? this.element.businessTransactionId ?? this.element.id;
    this.form.get('serviceRequestId')?.setValue(serviceRequestId);
  }

  private loadChargeInformations(): void {
    const serviceRequestCode = this.element?.serviceRequestCode ?? this.getDefaultServiceRequestCode();

    const chargeInfo$ = this.chargeInformationService.chargeInformationQueryPost({
      chargeType: "Fee",
      dataHubStatus: "Confirmed",
      latest: true,
      page: 1,
      pageSize: 20,
      validFrom: new Date().toISOString().split('T')[0]
    }).pipe(
      catchError(error => {
        console.error('Error fetching charge information:', error);
        return of({ results: [] });
      })
    );

    const standardChargeInfo$: Observable<string | null> = serviceRequestCode
      ? this.serviceRequestService.serviceRequestGetStandardChargeInformationIdForServiceRequestCodeGet(serviceRequestCode).pipe(
        catchError(error => {
          console.error('Error fetching default charge information ID:', error);
          return of(null);
        })
      )
      : of(null);

    forkJoin([chargeInfo$, standardChargeInfo$]).subscribe(([chargeInfoResponse, standardChargeInformationId]) => {
      this.chargeInformations = chargeInfoResponse.results ?? [];
      this.setChargeInformationValue(standardChargeInformationId);
    });
  }

  private setChargeInformationValue(standardChargeInformationId: any): void {
    const chargeInformationControl = this.form.get('chargeInformationId');
    if (!standardChargeInformationId) {
      chargeInformationControl?.setValue(null);
      return;
    }
    const existsInList = this.chargeInformations?.some(ci => ci.id === standardChargeInformationId);
    chargeInformationControl?.setValue(existsInList ? standardChargeInformationId : null);
  }

  private getDefaultServiceRequestCode(): ServiceRequestCode | undefined {
    switch (this.actionType) {
      case 'disconnect':
        return ServiceRequestCode.Disconnect;
      case 'reconnect':
        return ServiceRequestCode.Connect;
      case 'closeDown':
        return ServiceRequestCode.CloseDown;
      case 'disconnectDueToEndOfSupply':
        return ServiceRequestCode.DisconnectDueToEndOfSupply;
      default:
        return undefined;
    }
  }

  public submit(): void {
    const data = { ...this.form.value };
    if (this.environment.featureFlags.chargeInformationFee.enabled || data.response === UserResponse.Rejected) {
      data.chargeInformationId = null;
    }

    this.formApiService.submit(
      this.form,
      () => this.serviceRequestService.serviceRequestSubmitUserRequestPost(data),
      {
        closeModal: true,
        successMessage: 'serviceRequest.reply.success'
      })
  }
}
